import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import "./DocumentManagement.css";
import { logInteraction } from "../utils/logInteraction";
import Select from "react-select";

axios.defaults.withCredentials = true;

function DocumentManagement({ user }) {
    useEffect(() => {
        logInteraction(
            "page_view",
            { viewport: { width: window.innerWidth, height: window.innerHeight } },
            user
        );
    }, [user]);

    const [showDupModal, setShowDupModal] = useState(false);
    const [dupMatches, setDupMatches] = useState([]);
    const [dupCheckPending, setDupCheckPending] = useState(false);
    const [dupAckKey, setDupAckKey] = useState(null);

    const [file, setFile] = useState(null);
    const fileInputRef = useRef(null);

    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);

    const [showMinutesModal, setShowMinutesModal] = useState(false);
    const [minutesRawText, setMinutesRawText] = useState("");

    const [dropdowns, setDropdowns] = useState({
        scopes: [],
        constitutions: [],
        countries: [],
        orderCodes: [],
        lodges: [],
        eventTypes: [],
        documentTypes: [],
        fileTypes: [],
        roles: [],
    });

    const getInitialFormData = useCallback(
        (roles = []) => ({
            scope: "LOD",
            constitution: "",
            country: "",
            orderCode: "",
            lodge: "",
            eventType: "",
            documentType: "",
            fileType: "",
            date: "",
            version: "1",
            addendum: "",
            description: "",
            author_initials: "",
            doi: "",
            title: "",
            roles: roles.map((r) => r.value),
        }),
        []
    );

    const [formData, setFormData] = useState(getInitialFormData([]));
    const [errors, setErrors] = useState({});

    const DUP_FIELDS = useMemo(
        () =>
            new Set([
                "constitution",
                "country",
                "orderCode",
                "lodge",
                "date",
                "eventType",
                "documentType",
            ]),
        []
    );

    const currentUser = useMemo(() => {
        if (user && (user.id || user.userId)) {
            return { ...user, id: user.id ?? user.userId };
        }
        try {
            const stored = JSON.parse(localStorage.getItem("user"));
            if (stored?.id || stored?.userId) {
                return { ...stored, id: stored.id ?? stored.userId };
            }
        } catch {
            // ignore
        }
        return null;
    }, [user]);

    const uploaderDisplay = useMemo(() => {
        if (!currentUser) return "Unknown";
        const name =
            [currentUser.first_name, currentUser.last_name].filter(Boolean).join(" ") ||
            currentUser.username ||
            currentUser.email;
        return name || "Unknown";
    }, [currentUser]);

    const pickIfValid = (value, options) =>
        value && Array.isArray(options) && options.some((o) => o.value === value) ? value : "";

    const generateTitleAndDescription = useCallback((data, dd) => {
        const { lodge, documentType, eventType, date, constitution } = data;

        const lodgeDetails = dd.lodges.find((l) => l.value === lodge);
        const lodgeName = lodgeDetails?.label || "Unknown Lodge";

        const docTypeName =
            dd.documentTypes.find((d) => d.value === documentType)?.label ||
            "Unknown Document";

        const eventTypeName =
            dd.eventTypes.find((e) => e.value === eventType)?.label ||
            "Unknown Event";

        const constitutionName =
            dd.constitutions.find((c) => c.value === constitution)?.label ||
            "Unknown Constitution";

        const formattedDate = date
            ? new Date(date).toISOString().split("T")[0]
            : "No Date";

        const generatedTitle = `${docTypeName}: ${lodgeName} ${constitutionName} - ${eventTypeName} - ${formattedDate}`;
        const generatedDescription = `${docTypeName} for ${lodgeName} ${constitutionName}'s ${eventTypeName} on ${formattedDate}.`;

        return { title: generatedTitle, description: generatedDescription };
    }, []);

    useEffect(() => {
        const fetchDropdownData = async () => {
            try {
                const [
                    scopes,
                    constitutions,
                    countries,
                    orderCodes,
                    lodges,
                    eventTypes,
                    documentTypes,
                    fileTypes,
                    roles,
                ] = await Promise.all([
                    axios.get("/api/scopes"),
                    axios.get("/api/constitutions"),
                    axios.get("/api/countries"),
                    axios.get("/api/order-codes"),
                    axios.get("/api/lodges"),
                    axios.get("/api/event-types"),
                    axios.get("/api/document-types"),
                    axios.get("/api/file-types"),
                    axios.get("/api/roles"),
                ]);

                const nextDropdowns = {
                    scopes: scopes.data
                        .map((scope) => ({ value: scope.code, label: scope.description }))
                        .sort((a, b) => a.label.localeCompare(b.label)),

                    constitutions: constitutions.data
                        .map((constitution) => ({
                            value: constitution.code,
                            label: constitution.description,
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label)),

                    countries: countries.data
                        .map((country) => ({ value: country.code, label: country.name }))
                        .sort((a, b) => a.label.localeCompare(b.label)),

                    orderCodes: orderCodes.data
                        .map((orderCode) => ({
                            value: orderCode.code,
                            label: orderCode.description,
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label)),

                    lodges: lodges.data
                        .map((lodge) => ({ value: lodge.code, label: lodge.name }))
                        .sort((a, b) => a.label.localeCompare(b.label)),

                    eventTypes: eventTypes.data
                        .map((eventType) => ({
                            value: eventType.code,
                            label: eventType.description,
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label)),

                    documentTypes: documentTypes.data
                        .map((docType) => ({
                            value: docType.code,
                            label: docType.description,
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label)),

                    fileTypes: fileTypes.data
                        .map((fileType) => ({
                            value: fileType.code,
                            label: fileType.description,
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label)),

                    roles: roles.data
                        .map((role) => ({ value: role.id, label: role.name }))
                        .sort((a, b) => a.label.localeCompare(b.label)),
                };

                setDropdowns(nextDropdowns);

                const PRESETS = {
                    constitution: "IC",
                    country: "ZAF",
                    orderCode: "003",
                };

                setFormData((prev) => {
                    const next = {
                        ...getInitialFormData(nextDropdowns.roles),
                        ...prev,
                        constitution:
                            pickIfValid(PRESETS.constitution, nextDropdowns.constitutions) ||
                            prev.constitution,
                        country:
                            pickIfValid(PRESETS.country, nextDropdowns.countries) ||
                            prev.country,
                        orderCode:
                            pickIfValid(PRESETS.orderCode, nextDropdowns.orderCodes) ||
                            prev.orderCode,
                        roles: nextDropdowns.roles.map((role) => role.value),
                    };

                    const { title, description } = generateTitleAndDescription(next, nextDropdowns);
                    return { ...next, title, description };
                });
            } catch (error) {
                console.error("Error fetching dropdown data:", error);
            }
        };

        fetchDropdownData();
    }, [generateTitleAndDescription, getInitialFormData]);

    const resetFormAfterUpload = useCallback(() => {
        const resetBase = {
            ...getInitialFormData(dropdowns.roles),
            constitution: pickIfValid("IC", dropdowns.constitutions),
            country: pickIfValid("ZAF", dropdowns.countries),
            orderCode: pickIfValid("003", dropdowns.orderCodes),
        };

        const { title, description } = generateTitleAndDescription(resetBase, dropdowns);

        setFormData({
            ...resetBase,
            title,
            description,
        });

        setFile(null);
        setErrors({});
        setUploadProgress(0);
        setDupAckKey(null);
        setShowDupModal(false);
        setDupMatches([]);
        setMinutesRawText("");
        setShowMinutesModal(false);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    }, [dropdowns, generateTitleAndDescription, getInitialFormData]);

    const handleDropdownChange = (field, selectedOption) => {
        const value = selectedOption?.value || "";

        setFormData((prevData) => {
            const updatedData = { ...prevData, [field]: value };
            const { title, description } = generateTitleAndDescription(updatedData, dropdowns);
            return { ...updatedData, title, description };
        });

        if (DUP_FIELDS.has(field)) {
            setDupAckKey(null);
        }
    };

    const handleFileChange = (e) => {
        const nextFile = e.target.files?.[0] || null;
        setFile(nextFile);
        setErrors((prev) => ({ ...prev, file: "" }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === "scope") {
            setFormData((prev) => ({
                ...prev,
                [name]: value,
                orderCode: "",
                lodge: "",
            }));
            return;
        }

        if (name === "roles") {
            const options = Array.from(e.target.selectedOptions, (option) => option.value);
            setFormData((prevData) => ({
                ...prevData,
                roles: options,
            }));
            return;
        }

        setFormData((prev) => {
            const next = { ...prev, [name]: value };

            if (name === "date") {
                const { title, description } = generateTitleAndDescription(next, dropdowns);
                return { ...next, title, description };
            }

            return next;
        });

        if (DUP_FIELDS.has(name)) {
            setDupAckKey(null);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        const requiredFields = [
            "scope",
            "constitution",
            "country",
            "eventType",
            "documentType",
            "fileType",
            "date",
            "version",
            "title",
            "description",
        ];

        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = "This field is required";
            }
        });

        if (formData.scope === "LOD") {
            if (!formData.orderCode) {
                newErrors.orderCode = "Order Code is required when Scope is 'LOD'";
            }
            if (!formData.lodge) {
                newErrors.lodge = "Lodge is required when Scope is 'LOD'";
            }
        }

        if (formData.roles.length === 0) {
            newErrors.roles = "Please select at least one role.";
        }

        if (!file) {
            newErrors.file = "Please select a file to upload";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const buildDupParams = useCallback(() => ({
        constitution: formData.constitution || undefined,
        country: formData.country || undefined,
        orderCode: formData.orderCode || undefined,
        entity: formData.lodge || undefined,
        date: formData.date || undefined,
        eventType: formData.eventType || undefined,
        documentType: formData.documentType || undefined,
    }), [formData]);

    const checkForDuplicate = useCallback(async () => {
        const params = buildDupParams();
        const allSet = Object.values(params).every(Boolean);
        if (!allSet) return { matches: [] };

        setDupCheckPending(true);
        try {
            const { data } = await axios.get("/api/documents/exists", { params });
            return { matches: Array.isArray(data?.matches) ? data.matches : [] };
        } catch (e) {
            console.warn("Duplicate check failed", e?.message || e);
            return { matches: [] };
        } finally {
            setDupCheckPending(false);
        }
    }, [buildDupParams]);

    const proceedUpload = useCallback(async () => {
        const data = new FormData();

        Object.keys(formData).forEach((key) => {
            if (key === "roles") {
                data.append(key, formData[key].join(","));
            } else {
                data.append(key, formData[key]);
            }
        });

        data.append("document", file);

        if (currentUser?.id != null) {
            data.append("uploader_id", String(currentUser.id));
            const displayName =
                [currentUser.first_name, currentUser.last_name].filter(Boolean).join(" ") ||
                currentUser.username ||
                currentUser.email ||
                "Unknown";
            data.append("uploader_display", displayName);
        } else {
            console.warn("[DM] No currentUser.id resolved — uploader fields will be blank");
        }

        setIsUploading(true);
        setUploadProgress(0);

        try {
            const response = await axios.post("/api/upload", data, {
                headers: { "Content-Type": "multipart/form-data" },
                withCredentials: true,
                timeout: 0,
                onUploadProgress: (progressEvent) => {
                    if (progressEvent.total) {
                        const percentCompleted = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                        setUploadProgress(percentCompleted);
                    }
                },
            });

            alert(response.data.message);
            resetFormAfterUpload();
        } catch (error) {
            console.error("Error during file upload:");
            console.error("Error message:", error.message);

            if (error.response) {
                console.error("Response status:", error.response.status);
                console.error("Response data:", error.response.data);
                console.error("Response headers:", error.response.headers);
            } else if (error.request) {
                console.error("No response received from the server:", error.request);
            } else {
                console.error("Error setting up the request:", error.message);
            }

            alert(
                error.response?.data?.error
                    ? `File upload failed: ${error.response.data.error}`
                    : "File upload failed due to an unknown error."
            );
        } finally {
            setIsUploading(false);
        }
    }, [formData, file, currentUser, resetFormAfterUpload]);

    const currentDupKey = useMemo(() => JSON.stringify(buildDupParams()), [buildDupParams]);

    useEffect(() => {
        const params = buildDupParams();
        const allSet = Object.values(params).every(Boolean);
        if (!allSet) return;
        if (isUploading) return;
        if (showDupModal) return;
        if (currentDupKey === dupAckKey) return;

        const t = setTimeout(async () => {
            const { matches } = await checkForDuplicate();
            if (matches?.length) {
                setDupMatches(matches);
                setShowDupModal(true);
            }
        }, 400);

        return () => clearTimeout(t);
    }, [currentDupKey, dupAckKey, isUploading, showDupModal, checkForDuplicate, buildDupParams]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        await proceedUpload();
    };

    const REQUIRED_FOR_TEXT_BASE = ["documentType", "eventType", "lodge", "fileType"];

    const hasValue = (v) => v !== undefined && v !== null && String(v).trim() !== "";

    const fieldClass = (val) => (hasValue(val) ? "field-has-value" : "field-missing-value");

    const coreDropdownsSelected = (fd) => {
        const requiredList =
            fd.scope === "LOD"
                ? [...REQUIRED_FOR_TEXT_BASE, "date"]
                : [...REQUIRED_FOR_TEXT_BASE.filter((f) => f !== "lodge"), "date"];

        return requiredList.every((k) => hasValue(fd[k]));
    };

    const textFieldClass = (textVal, fd) => {
        return coreDropdownsSelected(fd) && hasValue(textVal)
            ? "field-has-value"
            : "field-missing-value";
    };

    const allTextReady = (fd) =>
        coreDropdownsSelected(fd) && hasValue(fd.title) && hasValue(fd.description);

    const isMinutesSelected = () => {
        const opt = dropdowns.documentTypes.find((d) => d.value === formData.documentType);
        return !!opt && /minutes?/i.test(opt.label || "");
    };

    function parseMinutesSynopsis(raw) {
        if (!raw) return null;

        const lines = raw
            .replace(/\r\n?/g, "\n")
            .split("\n")
            .map((l) => l.trim())
            .filter((l) => l && !/^===/.test(l));

        const map = {};
        const points = [];

        const put = (k, v) => {
            if (!k) return;
            const key = k.replace(/_/g, " ").replace(/\s+/g, " ").toLowerCase();
            const alias = {
                "lodge": "lodge",
                "date": "date",
                "wm presiding": "wm",
                "w m presiding": "wm",
                "wm": "wm",
                "venue": "venue",
                "secretary": "secretary",
                "benevolence collected": "benevolence",
                "benevolence collection": "benevolence",
                "benevolence": "benevolence",
                "attendees": "attendees",
                "attendeesmembers 12": "attendees",
                "attendeesmembers": "attendees",
                "relevant points": "relevant points",
            };
            const norm = alias[key] || key;
            map[norm] = (map[norm] ? map[norm] + " " : "") + v.trim();
        };

        for (const line of lines) {
            const p = line.match(/^(\d+)\s*[:.)-]\s*(.+)$/);
            if (p) {
                points.push(p[2].trim());
                continue;
            }

            const m = line.match(/^([^:]+)::?\s*(.*)$/);
            if (m) {
                const k = m[1].trim();
                const v = m[2].trim();
                if (k) put(k, v);
                continue;
            }

            if (/relevant\s+points/i.test(line)) {
                const multi = [...line.matchAll(/\d+\s*[:.)-]\s*([^]+?)(?=\s+\d+\s*[:.)-]\s*|$)/g)]
                    .map((mm) => mm[1].trim());
                if (multi.length) points.push(...multi);
            }
        }

        let attendeesOut = map["attendees"] || "";
        const members = attendeesOut.match(/members?\s*[:=]\s*(\d+)/i)?.[1];
        const visitors = attendeesOut.match(/visitors?\s*[:=]\s*(\d+)/i)?.[1];
        if (members || visitors) {
            attendeesOut = `Members: ${members ?? "—"}, Visitors: ${visitors ?? "—"}`;
        }

        return {
            lodge: map["lodge"] || "",
            date: map["date"] || "",
            wm: map["wm"] || "",
            venue: map["venue"] || "",
            attendees: attendeesOut,
            secretary: map["secretary"] || "",
            benevolence: map["benevolence"] || "",
            points,
        };
    }

    const buildMinutesDescription = (parsed, context = {}) => {
        const {
            wm = "",
            venue = "",
            attendees = "",
            secretary = "",
            benevolence = "",
            points = [],
        } = parsed || {};

        const { lodgeName = "", isoDate = "", eventTypeLabel = "" } = context;

        const pts =
            points.length > 0
                ? points.map((p, i) => ` ${i + 1}) ${p}`).join("\n")
                : " 1) —";

        const lines = [
            lodgeName ? `Lodge: ${lodgeName}.` : null,
            isoDate ? `Date: ${isoDate}.` : null,
            eventTypeLabel ? `Event: ${eventTypeLabel}.` : null,
            wm ? `WM Presiding: ${wm}.` : null,
            venue ? `Venue: ${venue}.` : null,
            attendees ? `Attendees: ${attendees}.` : null,
            secretary ? `Secretary: ${secretary}.` : null,
            "Relevant Points:",
            pts,
            benevolence ? `Benevolence Collected: ${benevolence}.` : null,
        ];

        return lines.filter(Boolean).join("\n");
    };

    const openMinutesModal = () => {
        if (!isMinutesSelected() || !allTextReady(formData)) return;
        setMinutesRawText("");
        setShowMinutesModal(true);
    };

    const closeMinutesModal = () => setShowMinutesModal(false);

    const applyMinutesDescription = () => {
        const parsed = parseMinutesSynopsis(minutesRawText);

        const lodgeOpt = dropdowns.lodges.find((l) => l.value === formData.lodge);
        const eventTypeOpt = dropdowns.eventTypes.find((e) => e.value === formData.eventType);

        const finalDesc = buildMinutesDescription(parsed, {
            lodgeName: lodgeOpt?.label || "",
            isoDate: formData.date ? new Date(formData.date).toISOString().split("T")[0] : "",
            eventTypeLabel: eventTypeOpt?.label || "",
        });

        setFormData((prev) => ({ ...prev, description: finalDesc }));
        setShowMinutesModal(false);
        setMinutesRawText("");
    };

    const selectedLabel = (key, value) =>
        dropdowns[key]?.find((item) => item.value === value)?.label || "";

    const roleLabels = dropdowns.roles
        .filter((role) => formData.roles.includes(role.value))
        .map((role) => role.label);

    const metadataChecks = [
        { label: "Constitution", complete: hasValue(formData.constitution) },
        { label: "Country", complete: hasValue(formData.country) },
        { label: "Order code", complete: formData.scope !== "LOD" || hasValue(formData.orderCode) },
        { label: "Lodge / entity", complete: formData.scope !== "LOD" || hasValue(formData.lodge) },
        { label: "Date", complete: hasValue(formData.date) },
        { label: "Event type", complete: hasValue(formData.eventType) },
        { label: "Document type", complete: hasValue(formData.documentType) },
        { label: "File type", complete: hasValue(formData.fileType) },
        { label: "Title", complete: hasValue(formData.title) },
        { label: "Description", complete: hasValue(formData.description) },
        { label: "Access roles", complete: formData.roles.length > 0 },
        { label: "File selected", complete: !!file },
    ];

    const completedChecks = metadataChecks.filter((item) => item.complete).length;
    const requiredComplete = completedChecks === metadataChecks.length;

    const duplicateStatus = dupCheckPending
        ? "Checking duplicates"
        : dupAckKey === currentDupKey
            ? "Duplicate reviewed"
            : "Auto-check ready";

    const previewTitle = formData.title || "Title will be generated from the selected metadata";
    const previewDescription =
        formData.description || "Description will appear here once the key fields are selected.";

    const onDropFile = (event) => {
        event.preventDefault();
        const nextFile = event.dataTransfer.files?.[0] || null;
        if (nextFile) {
            setFile(nextFile);
            setErrors((prev) => ({ ...prev, file: "" }));
        }
    };

    return (
        <main className="upload-workspace">
            <section className="upload-hero">
                <div>
                    <p className="upload-eyebrow">Archive intake</p>
                    <h1>Upload Documents</h1>
                    <p>
                        Capture clean metadata, check for existing records, set access, and upload the original file in one focused workspace.
                    </p>
                </div>
                <div className="upload-hero-actions">
                    <span>{uploaderDisplay}</span>
                    <button type="button" className="upload-secondary-btn" onClick={resetFormAfterUpload} disabled={isUploading}>
                        Reset form
                    </button>
                </div>
            </section>

            <section className="upload-status-grid" aria-label="Upload readiness">
                <div className="upload-stat-card">
                    <span>Readiness</span>
                    <strong>{completedChecks}/{metadataChecks.length}</strong>
                    <small>{requiredComplete ? "Ready to upload" : "Complete the highlighted fields"}</small>
                </div>
                <div className="upload-stat-card">
                    <span>Duplicate check</span>
                    <strong>{duplicateStatus}</strong>
                    <small>Exact metadata is checked automatically</small>
                </div>
                <div className="upload-stat-card">
                    <span>Access</span>
                    <strong>{formData.roles.length}</strong>
                    <small>{formData.roles.length === 1 ? "role selected" : "roles selected"}</small>
                </div>
                <div className="upload-stat-card">
                    <span>File</span>
                    <strong>{file ? "Selected" : "Missing"}</strong>
                    <small>{file ? `${file.name} (${(file.size / 1024 / 1024).toFixed(1)} MB)` : "Choose the original document"}</small>
                </div>
            </section>

            <form className="upload-layout" onSubmit={handleSubmit}>
                <div className="upload-main-column">
                    <section className="upload-panel">
                        <div className="upload-panel-heading">
                            <div>
                                <p>Step 1</p>
                                <h2>Core Metadata</h2>
                            </div>
                            <span className="upload-pill">Defaults loaded</span>
                        </div>

                        <div className="upload-field-grid">
                            <div className={`upload-field ${fieldClass(formData.constitution)}`}>
                                <label>Constitution</label>
                                <Select
                                    name="constitution"
                                    options={dropdowns.constitutions}
                                    value={dropdowns.constitutions.find((item) => item.value === formData.constitution) || null}
                                    onChange={(selectedOption) => handleDropdownChange("constitution", selectedOption)}
                                    placeholder="Select Constitution"
                                    isClearable
                                />
                                {errors.constitution && <span className="error3">{errors.constitution}</span>}
                            </div>

                            <div className={`upload-field ${fieldClass(formData.country)}`}>
                                <label>Country</label>
                                <Select
                                    name="country"
                                    options={dropdowns.countries}
                                    value={dropdowns.countries.find((item) => item.value === formData.country) || null}
                                    onChange={(selectedOption) => handleDropdownChange("country", selectedOption)}
                                    placeholder="Select Country"
                                    isClearable
                                />
                                {errors.country && <span className="error3">{errors.country}</span>}
                            </div>

                            <div className={`upload-field ${fieldClass(formData.orderCode)}`}>
                                <label>Order Code</label>
                                <Select
                                    name="orderCode"
                                    options={dropdowns.orderCodes}
                                    value={dropdowns.orderCodes.find((item) => item.value === formData.orderCode) || null}
                                    onChange={(selectedOption) => handleDropdownChange("orderCode", selectedOption)}
                                    placeholder="Select Order Code"
                                    isClearable
                                />
                                {errors.orderCode && <span className="error3">{errors.orderCode}</span>}
                            </div>

                            <div className={`upload-field upload-field-wide ${fieldClass(formData.lodge)}`}>
                                <label>Entity / Lodge</label>
                                <Select
                                    name="lodge"
                                    options={dropdowns.lodges}
                                    value={dropdowns.lodges.find((item) => item.value === formData.lodge) || null}
                                    onChange={(selectedOption) => handleDropdownChange("lodge", selectedOption)}
                                    placeholder="Select Lodge"
                                    isClearable
                                />
                                {errors.lodge && <span className="error3">{errors.lodge}</span>}
                            </div>
                        </div>
                    </section>

                    <section className="upload-panel">
                        <div className="upload-panel-heading">
                            <div>
                                <p>Step 2</p>
                                <h2>Document Details</h2>
                            </div>
                            <span className="upload-pill">{isMinutesSelected() ? "Minutes helper available" : "Standard document"}</span>
                        </div>

                        <div className="upload-field-grid">
                            <div className={`upload-field ${fieldClass(formData.date)}`}>
                                <label>Date</label>
                                <input type="date" name="date" value={formData.date} onChange={handleInputChange} required />
                                {errors.date && <span className="error3">{errors.date}</span>}
                            </div>

                            <div className={`upload-field ${fieldClass(formData.eventType)}`}>
                                <label>Event Type</label>
                                <Select
                                    name="eventType"
                                    options={dropdowns.eventTypes}
                                    value={dropdowns.eventTypes.find((item) => item.value === formData.eventType) || null}
                                    onChange={(selectedOption) => handleDropdownChange("eventType", selectedOption)}
                                    placeholder="Select Event Type"
                                    isClearable
                                />
                                {errors.eventType && <span className="error3">{errors.eventType}</span>}
                            </div>

                            <div className={`upload-field ${fieldClass(formData.documentType)}`}>
                                <label>Document Type</label>
                                <Select
                                    name="documentType"
                                    options={dropdowns.documentTypes}
                                    value={dropdowns.documentTypes.find((item) => item.value === formData.documentType) || null}
                                    onChange={(selectedOption) => handleDropdownChange("documentType", selectedOption)}
                                    placeholder="Select Document Type"
                                    isClearable
                                />
                                {errors.documentType && <span className="error3">{errors.documentType}</span>}
                            </div>

                            <div className={`upload-field ${fieldClass(formData.fileType)}`}>
                                <label>File Type</label>
                                <Select
                                    name="fileType"
                                    options={dropdowns.fileTypes}
                                    value={dropdowns.fileTypes.find((item) => item.value === formData.fileType) || null}
                                    onChange={(selectedOption) => setFormData((prev) => ({ ...prev, fileType: selectedOption?.value || "" }))}
                                    placeholder="Select File Type"
                                    isClearable
                                    isSearchable
                                />
                                {errors.fileType && <span className="error3">{errors.fileType}</span>}
                            </div>

                            <div className={`upload-field ${fieldClass(formData.version)}`}>
                                <label>Version</label>
                                <input type="text" name="version" value={formData.version} onChange={handleInputChange} required />
                                {errors.version && <span className="error3">{errors.version}</span>}
                            </div>

                            <div className="upload-field">
                                <label>Addendum</label>
                                <input type="text" name="addendum" value={formData.addendum} onChange={handleInputChange} placeholder="Optional" />
                            </div>

                            <div className="upload-field">
                                <label>Authors</label>
                                <input type="text" name="author_initials" value={formData.author_initials} onChange={handleInputChange} placeholder="Optional" />
                            </div>

                            <div className="upload-field">
                                <label>DOI</label>
                                <input type="text" name="doi" value={formData.doi} onChange={handleInputChange} placeholder="Optional" />
                            </div>
                        </div>
                    </section>

                    <section className="upload-panel">
                        <div className="upload-panel-heading">
                            <div>
                                <p>Step 3</p>
                                <h2>Title, Description & Access</h2>
                            </div>
                            <button
                                type="button"
                                className="upload-secondary-btn"
                                onClick={openMinutesModal}
                                disabled={!(isMinutesSelected() && allTextReady(formData))}
                                title={
                                    isMinutesSelected()
                                        ? allTextReady(formData)
                                            ? "Generate or replace the description from a pasted Lodge Minutes Synopsis."
                                            : "Complete the required fields before generating."
                                        : "This action is available only when Document Type is Minutes."
                                }
                            >
                                Generate Minutes Description
                            </button>
                        </div>

                        <div className="upload-field-grid single">
                            <div className={`upload-field ${textFieldClass(formData.title, formData)}`}>
                                <label>Title</label>
                                <input type="text" name="title" value={formData.title} onChange={handleInputChange} required />
                                {errors.title && <span className="error3">{errors.title}</span>}
                            </div>

                            <div className={`upload-field ${textFieldClass(formData.description, formData)}`}>
                                <label>Description</label>
                                <textarea name="description" value={formData.description} onChange={handleInputChange} required />
                                {errors.description && <span className="error3">{errors.description}</span>}
                            </div>

                            <div className={`upload-field ${fieldClass(formData.roles?.length ? "x" : "")}`}>
                                <label>Access Roles</label>
                                <Select
                                    name="roles"
                                    options={dropdowns.roles}
                                    isMulti
                                    value={dropdowns.roles.filter((role) => formData.roles.includes(role.value))}
                                    onChange={(selectedOptions) => {
                                        setFormData((prev) => ({
                                            ...prev,
                                            roles: selectedOptions.map((option) => option.value),
                                        }));
                                    }}
                                    placeholder="Select Roles"
                                    classNamePrefix="react-select3"
                                />
                                {errors.roles && <span className="error3">{errors.roles}</span>}
                            </div>
                        </div>
                    </section>
                </div>

                <aside className="upload-side-column">
                    <section className="upload-panel sticky">
                        <div className="upload-panel-heading">
                            <div>
                                <p>Step 4</p>
                                <h2>Original File</h2>
                            </div>
                        </div>

                        <div
                            className={`upload-dropzone ${file ? "has-file" : ""}`}
                            onDragOver={(event) => event.preventDefault()}
                            onDrop={onDropFile}
                        >
                            <input ref={fileInputRef} type="file" onChange={handleFileChange} required />
                            <strong>{file ? file.name : "Choose or drag a document"}</strong>
                            <span>{file ? `${(file.size / 1024 / 1024).toFixed(2)} MB selected` : "PDF, DOC, DOCX, images, and archive files are accepted by the server."}</span>
                            {errors.file && <span className="error3">{errors.file}</span>}
                        </div>

                        {isUploading && (
                            <div className="progress-container">
                                <div className="upload-progress-label">
                                    <span>Uploading</span>
                                    <strong>{uploadProgress}%</strong>
                                </div>
                                <div className="progress-bar">
                                    <div className="progress-bar-fill" style={{ width: `${uploadProgress}%` }}></div>
                                </div>
                            </div>
                        )}

                        <button className="upload-submit-btn" type="submit" disabled={isUploading || dupCheckPending}>
                            {isUploading ? "Uploading..." : "Upload Document"}
                        </button>

                        <div className="upload-checklist">
                            {metadataChecks.map((item) => (
                                <div className={item.complete ? "complete" : ""} key={item.label}>
                                    <span>{item.complete ? "OK" : "-"}</span>
                                    {item.label}
                                </div>
                            ))}
                        </div>
                    </section>

                    <section className="upload-panel upload-review-card">
                        <div className="upload-panel-heading compact">
                            <div>
                                <p>Review</p>
                                <h2>Archive Record</h2>
                            </div>
                        </div>
                        <h3>{previewTitle}</h3>
                        <p>{previewDescription}</p>
                        <dl>
                            <div><dt>Lodge</dt><dd>{selectedLabel("lodges", formData.lodge) || "Not selected"}</dd></div>
                            <div><dt>Type</dt><dd>{selectedLabel("documentTypes", formData.documentType) || "Not selected"}</dd></div>
                            <div><dt>Event</dt><dd>{selectedLabel("eventTypes", formData.eventType) || "Not selected"}</dd></div>
                            <div><dt>Date</dt><dd>{formData.date || "Not selected"}</dd></div>
                            <div><dt>Access</dt><dd>{roleLabels.length ? roleLabels.join(", ") : "No roles selected"}</dd></div>
                        </dl>
                    </section>
                </aside>
            </form>

            {showMinutesModal && (
                <div className="upload-modal-backdrop">
                    <div className="upload-modal">
                        <div className="upload-modal-header">
                            <div>
                                <p>Minutes helper</p>
                                <h2>Paste Lodge Minutes Synopsis</h2>
                            </div>
                            <button type="button" onClick={closeMinutesModal} aria-label="Close">x</button>
                        </div>

                        <textarea
                            className="upload-monospace-area"
                            value={minutesRawText}
                            onChange={(e) => setMinutesRawText(e.target.value)}
                            placeholder={`Example:\nLodge Minutes Synopsis\nLodge\tPTA Leinster 743\nDate\t12-Feb-08\nWM Presiding\tBlake Beilings\nVenue\tCentral East Rand Temple\nAttendees\tMembers: 12,  Visitors: 6\nSecretary\tMichael Vilbert\nRelevant Points\t1) Brother Mark Mackenzie donated R2000  2) Deposit was paid for the Barnyard function in November  3) Bro. Joe Soap was raised...\nBenevolence Collected\tR470`}
                        />

                        <div className="upload-modal-actions">
                            <button className="upload-secondary-btn" type="button" onClick={closeMinutesModal}>
                                Cancel
                            </button>
                            <button
                                className="upload-primary-btn"
                                type="button"
                                onClick={applyMinutesDescription}
                                disabled={!minutesRawText.trim()}
                            >
                                Use This Text
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showDupModal && (
                <div className="upload-modal-backdrop">
                    <div className="upload-modal duplicate">
                        <div className="upload-modal-header">
                            <div>
                                <p>Duplicate protection</p>
                                <h2>Possible Duplicate Found</h2>
                            </div>
                        </div>

                        <p>
                            A document with the same constitution, country, order code, lodge, date, event type, and document type already exists.
                            Review it before continuing.
                        </p>

                        <ul className="upload-duplicate-list">
                            {dupMatches.map((m) => {
                                return (
                                    <li key={m.id}>
                                        <div>
                                            <code>{m.id}</code>
                                            <strong>{m.title || "Untitled document"}</strong>
                                        </div>
                                        <div className="upload-duplicate-actions">
                                            <button
                                                type="button"
                                                className="upload-secondary-btn"
                                                onClick={() => navigator.clipboard?.writeText(m.id)}
                                                title="Copy ID"
                                            >
                                                Copy
                                            </button>
                                            <a className="upload-secondary-btn" href={`/view-documents?documentId=${encodeURIComponent(m.id)}`} target="_blank" rel="noreferrer">
                                                View
                                            </a>
                                        </div>

                                        {(() => {
                                            const name = (m.uploaded_by_display || "").trim();
                                            const dateStr = m.uploaded_at
                                                ? new Date(m.uploaded_at).toISOString().split("T")[0]
                                                : "";

                                            if (!name) return null;

                                            return (
                                                <small>
                                                    Uploaded by {name}{dateStr ? ` • on ${dateStr}` : ""}
                                                </small>
                                            );
                                        })()}
                                    </li>
                                );
                            })}
                        </ul>

                        <div className="upload-modal-actions">
                            <button className="upload-secondary-btn" type="button" onClick={() => setShowDupModal(false)}>
                                Go Back
                            </button>
                            <button
                                className="upload-primary-btn"
                                type="button"
                                onClick={() => {
                                    setDupAckKey(currentDupKey);
                                    setShowDupModal(false);
                                }}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}

export default DocumentManagement;
